import { useEffect, useState } from 'react';

import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonCol,
  IonGrid,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonRow
} from '@ionic/react';
import { lockOpen } from 'ionicons/icons';

import styles from './SignInCard.module.scss';

import { State } from '../../constants/state';
import { Service } from '../../services/service';
import { PermissionView } from '../PermissionView/PermissionView';
import { ServiceLoader } from '../ServiceLoader/ServiceLoader';

export interface SignInCardProps {
  oauthUrl: string;
  onTokenSignInClicked: (token: string, username?: string) => void;
  service: Service;
  tokenUrl: string;
  useToken?: boolean;
}

export const SignInCard = ({
  service,
  oauthUrl,
  tokenUrl,
  useToken,
  onTokenSignInClicked
}: SignInCardProps) => {
  const [tokenInput, setTokenInput] = useState(useToken ?? false);
  const [username, setUsername] = useState<string>();
  const [token, setToken] = useState<string>();
  const [tokenSignInEnabled, setTokenSignInEnabled] = useState(false);

  useEffect(() => {
    if (!service.isCloud) setTokenInput(true);
  }, []);

  useEffect(() => {
    if (service.patUsername && token?.length && username?.length)
      setTokenSignInEnabled(true);
    else if (!service.patUsername && token?.length) setTokenSignInEnabled(true);
    else setTokenSignInEnabled(false);
  }, [token, username]);

  const handleUsernameInput = (event: Event) => {
    const value = (event.target as HTMLIonInputElement).value as string;
    if (!value) return;
    setUsername(value);
  };

  const handleTokenInput = (event: Event) => {
    const value = (event.target as HTMLIonInputElement).value as string;
    if (!value) return;
    setToken(value);
  };

  const handleTokenSignIn = () => {
    if (!token) return;
    onTokenSignInClicked(token, username);
  };

  return (
    <div className={styles.cardContainer}>
      <ServiceLoader service={service.id} state={State.Indeterminate} />
      <IonCard className={styles.card}>
        <IonCardHeader>
          <IonCardTitle>
            Sign into {service.name} with{' '}
            {tokenInput ? `a ${service.patName}` : `OAuth`}
          </IonCardTitle>
          <IonCardSubtitle>
            Your {tokenInput ? service.patName.toLowerCase() : `credentials`}{' '}
            will be securely stored locally and only accessed to make direct
            calls from your local machine to the {service.name} API
          </IonCardSubtitle>
        </IonCardHeader>
        <IonCardContent>
          <PermissionView
            permissions={
              tokenInput ? service.tokenPermissions : service.oauthPermissions
            }
            tokenUrl={tokenUrl}
            tokenInput={tokenInput}
            tokenName={service.patName}
          />

          {tokenInput && (
            <IonItem lines="none" key="paste-token">
              <IonIcon src="" name="key" slot="start"></IonIcon>

              <IonGrid>
                <IonRow>
                  <IonLabel class="username-container">
                    Enter your {service.patName}
                    {service.patUsername && (
                      <p>
                        Paste in your {service.patName.toLowerCase()} below and
                        click sign in
                      </p>
                    )}
                  </IonLabel>
                </IonRow>
                <IonRow>
                  {service.patUsername && service.patUsernameLink && (
                    <IonCol>
                      <IonItem>
                        <IonInput
                          placeholder="Username"
                          type="text"
                          value={username}
                          onIonInput={handleUsernameInput}
                        >
                          <IonLabel slot="end">
                            <a
                              className={styles.link}
                              href={service.patUsernameLink}
                              target="_blank"
                              rel="noreferrer"
                            >
                              View username
                            </a>
                          </IonLabel>
                        </IonInput>
                      </IonItem>
                    </IonCol>
                  )}

                  <IonCol>
                    <IonItem>
                      <IonInput
                        placeholder={service.placeholderPat}
                        type="text"
                        value={token}
                        onIonInput={handleTokenInput}
                      ></IonInput>
                    </IonItem>
                  </IonCol>
                </IonRow>
              </IonGrid>
            </IonItem>
          )}

          <IonItem lines="none">
            <IonIcon icon={lockOpen} color="medium" slot="start" />
            <IonLabel>
              <h2>
                Your data is protected by{' '}
                <span style={{ fontWeight: '600' }}>end-to-end encryption</span>
              </h2>
              <p className="description">
                When you share your working tree changes all sensitive data is
                encrypted, including source code, branch names and file paths.
                <p style={{ paddingTop: '4px' }}>
                  End-to-end encryption ensures only you and the other members
                  of your organization can read what is sent, and nobody in
                  between, not even GitLive.
                </p>
              </p>
            </IonLabel>
          </IonItem>
        </IonCardContent>
        <section className={styles.footer}>
          {tokenInput ? (
            <IonButton
              color="primary"
              fill="solid"
              expand="block"
              onClick={handleTokenSignIn}
              disabled={!tokenSignInEnabled}
            >
              Sign in with {service.patName.toLowerCase()}
            </IonButton>
          ) : (
            <IonButton
              color="primary"
              fill="solid"
              expand="block"
              href={oauthUrl}
            >
              Sign in with OAuth
            </IonButton>
          )}

          <hr />

          {service.isCloud && (
            <IonLabel class="ion-text-wrap text-margin">
              <a
                className={styles.link}
                onClick={() => setTokenInput((c) => !c)}
              >
                {tokenInput
                  ? 'Alternatively, sign in with OAuth'
                  : `Alternatively, sign in with ${service.patName.toLowerCase()}`}
              </a>
            </IonLabel>
          )}
        </section>
      </IonCard>
    </div>
  );
};
